import { PosicaoAtualAtivoItem } from '../types/carteiraDiaria';

/** Retorno diário esperado de um ativo. `cdiPct` é null quando a taxa do CDI não está disponível. */
export interface ExpectedReturn {
  /** Retorno diário em decimal, ex: 0.000620 */
  daily: number;
  /** Proporção do CDI diário, ex: 1.032 = 103,2% do CDI. Null se CDI não disponível. */
  cdiPct: number | null;
}

/**
 * Calcula o retorno diário esperado de um ativo com base em seu tipo de remuneração.
 *
 * - FIXO: converte taxa AA ou AM para taxa diária via capitalização composta.
 * - IND_PERCENT: retorno = taxa_indicador_diária × percentual.
 * - IND_SPREAD: retorno multiplicativo = (1 + ind) × (1 + spread)^(1/dias) - 1.
 *
 * CDI é necessário apenas para calcular `cdiPct`. Se ausente, `cdiPct` retorna null
 * mas `daily` ainda é calculado normalmente para FIXO.
 *
 * Retorna null se o ativo não tem remuneração configurada ou se o indicador
 * necessário para o cálculo do `daily` não estiver disponível.
 */
export const calcExpectedReturn = (
  ativo: PosicaoAtualAtivoItem,
  indicadoresDiarios: Record<string, number>,
): ExpectedReturn | null => {
  if (!ativo.rent_tipo || ativo.rent_valor == null) return null;

  const val = ativo.rent_valor / 100;
  const diasBase = ativo.rent_periodo === 'AM' ? 21 : 252;
  let daily: number;

  if (ativo.rent_tipo === 'FIXO') {
    daily = Math.pow(1 + val, 1 / diasBase) - 1;
  } else if (ativo.rent_tipo === 'IND_PERCENT') {
    const taxaInd = indicadoresDiarios[ativo.rent_indicador_codigo ?? ''];
    if (taxaInd == null) return null;
    daily = taxaInd * val;
  } else if (ativo.rent_tipo === 'IND_SPREAD') {
    const taxaInd = indicadoresDiarios[ativo.rent_indicador_codigo ?? ''];
    if (taxaInd == null) return null;
    daily = (1 + taxaInd) * Math.pow(1 + val, 1 / diasBase) - 1;
  } else {
    return null;
  }

  const cdiDiario = indicadoresDiarios['CDI'];
  const cdiPct = cdiDiario != null && cdiDiario !== 0 ? daily / cdiDiario : null;
  return { daily, cdiPct };
};

/** Formata uma proporção de CDI como "X,X% do CDI". Retorna null se cdiPct for null. */
export const formatCdiPct = (cdiPct: number | null): string | null => {
  if (cdiPct == null) return null;
  return `${(cdiPct * 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}% do CDI`;
};

/**
 * Calcula e formata o spread anual acima do CDI como "CDI + X,XX% a.a.".
 * Retorna null se daily ou cdiDiario não estiverem disponíveis.
 *
 * spread = ((1 + daily)^252 - 1) - ((1 + cdiDiario)^252 - 1)
 */
export const formatCdiSpread = (daily: number | null, cdiDiario: number | null): string | null => {
  if (daily == null || cdiDiario == null) return null;
  const spread = Math.pow((1 + daily) / (1 + cdiDiario), 252) - 1;
  const spreadPct      = spread * 100;
  const formatted      = Math.abs(spreadPct).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return spread >= 0 ? `CDI + ${formatted}% a.a.` : `CDI - ${formatted}% a.a.`;
};
